import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHOW_ALL_KEY = 'futurepath_show_all_tools';

const PathScopeContext = createContext(null);

export const PathScopeProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [scope, setScope] = useState(null);
  const [toolbox, setToolbox] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllTools, setShowAllTools] = useState(() => localStorage.getItem(SHOW_ALL_KEY) === 'true');

  const authConfig = useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  }), [token]);

  const refresh = useCallback(async () => {
    if (!user || !token) { setScope(null); setLoading(false); return; }
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API}/state/me`, authConfig),
        axios.get(`${API}/state/toolbox`, authConfig).catch(() => ({ data: null })),
      ]);
      setScope(s.data?.complete ? s.data : null);
      setToolbox(t.data);
    } catch {
      setScope(null);
    } finally {
      setLoading(false);
    }
  }, [user, token, authConfig]);

  const toggleModule = useCallback(async (key, on) => {
    const res = await axios.post(`${API}/state/toolbox`, { key, on }, authConfig);
    setScope(res.data);
    const t = await axios.get(`${API}/state/toolbox`, authConfig).catch(() => ({ data: null }));
    setToolbox(t.data);
    return res.data;
  }, [authConfig]);

  useEffect(() => { refresh(); }, [refresh]);

  const applyPreset = useCallback(async (presetKey) => {
    const res = await axios.post(`${API}/state/preset`, { preset_key: presetKey }, authConfig);
    setScope(res.data);
    return res.data;
  }, [authConfig]);

  const applySituations = useCallback(async (payload) => {
    const res = await axios.post(`${API}/state/me`, payload, authConfig);
    setScope(res.data);
    return res.data;
  }, [authConfig]);

  const clearScope = useCallback(async () => {
    await axios.delete(`${API}/state/me`, authConfig);
    setScope(null);
  }, [authConfig]);

  const toggleShowAllTools = useCallback(() => {
    setShowAllTools(prev => {
      localStorage.setItem(SHOW_ALL_KEY, String(!prev));
      return !prev;
    });
  }, []);

  const value = useMemo(() => ({
    loading,
    scope,
    toolbox,
    optionalModules: toolbox?.optional || [],
    toggleModule,
    hasScope: !!scope,
    pathLabel: scope?.preset_label || scope?.primary_archetype?.label || null,
    primaryArchetype: scope?.primary_archetype || scope?.archetypes?.[0] || null,
    archetypes: scope?.archetypes || [],
    modules: scope?.modules || [],
    allowedPaths: scope?.allowed_paths || [],
    showAllTools,
    toggleShowAllTools,
    applyPreset,
    applySituations,
    clearScope,
    refresh,
  }), [loading, scope, showAllTools, toggleShowAllTools, applyPreset, applySituations, clearScope, refresh, toolbox, toggleModule]);

  return <PathScopeContext.Provider value={value}>{children}</PathScopeContext.Provider>;
};

export const usePathScope = () => {
  const ctx = useContext(PathScopeContext);
  return ctx || {
    loading: false, scope: null, toolbox: null, optionalModules: [], toggleModule: async () => {},
    hasScope: false, pathLabel: null, primaryArchetype: null, archetypes: [], modules: [],
    allowedPaths: [], showAllTools: true, toggleShowAllTools: () => {}, applyPreset: async () => {},
    applySituations: async () => {}, clearScope: async () => {}, refresh: async () => {},
  };
};
